import { ref, computed } from "vue";
import apiClient from "./api";

const user = ref(getUserFromStorage());

// Export a computed property that components can use to get the current user value reactively
export const currentUser = computed(() => user.value);

// Export a computed property for admin status
export const currentUserIsAdmin = computed(() => user.value?.is_admin === true);

// Save user in localStorage
function saveUser(userData) {
  if (userData) {
    localStorage.setItem("user", JSON.stringify(userData));
  } else {
    localStorage.removeItem("user");
  }
  user.value = userData;
}

// Get user from localStorage
function getUserFromStorage() {
  const storedUser = localStorage.getItem("user");

  return storedUser ? JSON.parse(storedUser) : null;
}

// **Get user from VUO (backend) only if not already stored**
async function getUserFromVuo() {
  try {
    console.log("[authService] Fetching user from backend...");
    const response = await apiClient.getConnectedUser(); // Call the correct API endpoint

    console.log("[authService] Backend response status:", response.status);
    console.log("[authService] Backend response data:", response.data);

    if (response.status === 200) {
      const responseBody = response.data; // Axios automatically parses JSON

      if (responseBody.result === "OK") {
        let authorsObject = responseBody.message;
        console.log("[authService] User logged status:", authorsObject.logged);
        if (authorsObject.logged) {
          console.log("[authService] Saving user from backend:", authorsObject);
          saveUser(authorsObject);
          return authorsObject;
        } else {
          console.log("[authService] User not logged according to backend");
        }
      } else {
        console.log(
          "[authService] Backend result not OK:",
          responseBody.result,
        );
      }
    } else {
      console.log("[authService] Backend returned status:", response.status);
    }
  } catch (error) {
    console.error("[authService] Error fetching user from VUO:", error);
  }

  saveUser(null); // If fetching fails, clear the user data
  return null;
}

// Logout user - calls backend API and clears local state
async function logout() {
  try {
    await apiClient.logout();
  } catch (error) {
    console.error("[authService] Backend logout failed:", error);
  }

  // Clear local state
  localStorage.removeItem("user");
  user.value = null;
}

let lastVerification = 0;
const VERIFICATION_CACHE_MS = 30000;

let lastCookieLoginAttempt = 0;

async function isAuthenticated(force = false) {
  const now = Date.now();

  if (!user.value) {
    // No local user: the backend may still log us in from the VUO session
    // cookie (root_session / vuo_session) if the browser has one.
    if (!force && now - lastCookieLoginAttempt < VERIFICATION_CACHE_MS) {
      return false;
    }
    lastCookieLoginAttempt = now;
    const cookieUser = await getUserFromVuo();
    if (cookieUser) lastVerification = now;
    return !!cookieUser;
  }

  if (!force && now - lastVerification < VERIFICATION_CACHE_MS) {
    return true;
  }
  lastVerification = now;

  try {
    const response = await apiClient.getConnectedUser();
    if (response.status === 200 && response.data.result === "OK") {
      const userData = response.data.message;
      if (userData.logged) {
        saveUser(userData);
        return true;
      }
    }
    await logout();
    return false;
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      await logout();
      return false;
    }
    return !!user.value;
  }
}

// Redirect to the VUO login page. After login VUO sends the browser back to
// `returnUrl`, and the backend creates the Enodo session from the VUO cookie.
function redirectToVuoLogin(returnUrl = window.location.href) {
  const vuoPath =
    localStorage.getItem("development") === "false" ? "vuo" : "root";
  const proc = encodeURIComponent(new URL(returnUrl, window.location.origin).href);
  window.location.href = `https://vuo.elettra.eu/pls/${vuoPath}/vuo_secur.login?PROC=${proc}`;
}

// Export the authentication functions
export default {
  saveUser,
  getUserFromStorage,
  getUserFromVuo,
  logout,
  isAuthenticated,
  redirectToVuoLogin,
  currentUser,
  currentUserIsAdmin,
};
